import { Link } from '@remix-run/react'
import * as React from 'react'
import { mergeRefs } from 'react-merge-refs'
import useHighlightOnHover from '#app/utils/hooks/useHighlightOnHover'
import { cn } from '#app/utils/misc'

interface ChipProps extends React.HTMLAttributes<HTMLDivElement> {
	text: string
	linkTo?: string
	expandedStyle?: boolean
	altLinkStyle?: boolean
}

const BaseChip = React.forwardRef<
	HTMLDivElement,
	ChipProps & { hasLink: boolean }
>(({ text, hasLink, expandedStyle, altLinkStyle, className, ...props }, ref) => {
	const [hoverRef, isHovered, highlightedClassName] = useHighlightOnHover({
		ring: true,
	})

	return (
		<div
			ref={mergeRefs([ref, hoverRef])}
			className={cn(
				'inline-block h-auto rounded-sm text-xs',
				!expandedStyle && 'max-w-56',
				expandedStyle ? 'p-2' : 'p-1',
				expandedStyle && 'whitespace-pre-wrap',
				!hasLink
					? 'bg-gray-200 text-gray-600'
					: altLinkStyle
						? 'bg-purple-100 text-purple-500'
						: 'bg-blue-100 text-blue-500',
				hasLink && isHovered ? highlightedClassName : '',
				className,
			)}
			{...props}
		>
			<p
				className={cn('px-1', expandedStyle ? 'line-clamp-2' : 'line-clamp-1')}
			>
				{text}
			</p>
		</div>
	)
})
BaseChip.displayName = 'BaseChip'

const Chip = React.forwardRef<HTMLDivElement, ChipProps>(
	({ linkTo, ...props }, ref) => {
		return (
			<div className="align-baseline">
				{linkTo ? (
					<Link to={linkTo}>
						<BaseChip ref={ref} hasLink={true} {...props} />
					</Link>
				) : (
					<BaseChip ref={ref} hasLink={false} {...props} />
				)}
			</div>
		)
	},
)
Chip.displayName = 'Chip'

export default Chip
